import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

function normalizePhone(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('+45')) return digits;
  if (digits.startsWith('45') && digits.length === 10) return '+' + digits;
  if (digits.length === 8) return '+45' + digits;
  return digits;
}

/** Extract the raw 8-digit Danish number for flexible matching */
function stripToLocal(phone: string): string {
  const n = normalizePhone(phone);
  return n.startsWith('+45') ? n.slice(3) : n.replace(/^\+/, '');
}

interface CallerCustomer {
  id: string;
  name: string;
  contact_person: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
}

interface CallerProject {
  id: string;
  title: string;
  status: string;
  address: string | null;
}

export function useCallerLookup(phoneNumber: string | null) {
  const { user } = useAuth();
  const local = phoneNumber ? stripToLocal(phoneNumber) : null;

  const { data, isLoading } = useQuery({
    queryKey: ['caller-lookup', local],
    enabled: !!local && !!user?.company_id,
    staleTime: 30_000,
    queryFn: async () => {
      const companyId = user!.company_id!;
      const full = '+45' + local!;
      const searchVariants = [full, local!];

      // 1) Search customers by phone or mobile
      const { data: customers } = await supabase
        .from('customers')
        .select('id, name, contact_person, phone, mobile, email')
        .eq('company_id', companyId)
        .or(
          searchVariants.flatMap(t => [`phone.ilike.%${t}%`, `mobile.ilike.%${t}%`]).join(',')
        )
        .limit(1);

      if (customers?.length) {
        const customer = customers[0] as CallerCustomer;
        const { data: projects } = await supabase
          .from('projects')
          .select('id, title, status, address')
          .eq('customer_id', customer.id)
          .is('deleted_at', null)
          .not('status', 'in', '(completed,cancelled)')
          .order('created_at', { ascending: false })
          .limit(5);

        return { customer, projects: (projects || []) as CallerProject[] };
      }

      // 2) Fallback: search projects by contact_phone
      const { data: projectsByPhone } = await supabase
        .from('projects')
        .select('id, title, status, address, customer_id')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .or(searchVariants.map(t => `contact_phone.ilike.%${t}%`).join(','))
        .not('status', 'in', '(completed,cancelled)')
        .order('created_at', { ascending: false })
        .limit(5);

      if (projectsByPhone?.length) {
        // Try to get the customer from the first project
        const firstProject = projectsByPhone[0];
        let customer: CallerCustomer | null = null;

        if (firstProject.customer_id) {
          const { data: cust } = await supabase
            .from('customers')
            .select('id, name, contact_person, phone, mobile, email')
            .eq('id', firstProject.customer_id)
            .single();
          if (cust) customer = cust as CallerCustomer;
        }

        return {
          customer,
          projects: projectsByPhone.map(({ customer_id, ...p }) => p) as CallerProject[],
        };
      }

      return { customer: null, projects: [] };
    },
  });

  return {
    customer: data?.customer ?? null,
    projects: data?.projects ?? [],
    isLoading,
  };
}
